<mat-dialog-content>
<div class="time-sheet-wrapper" [class.popup-height]="popup">

  <!-- Always reserve space for calendar panel to prevent CLS -->
  <div class="left" [class.hidden-panel]="!calendarLoading && !hasGoogleAccess && !hasMicrosoftAccess">
    <!-- Show skeleton loader while checking calendar access -->
    @if (calendarLoading && !calendarLoaded) {
      <div class="calendar-skeleton">
        <div class="calendar-skeleton-header">
          <div class="skeleton-icon"></div>
          <div class="skeleton-title"></div>
        </div>
        <div class="calendar-skeleton-items">
          <div class="skeleton-item">
            <div class="skeleton-item-header">
              <div class="skeleton-source-icon"></div>
              <div class="skeleton-source-name"></div>
            </div>
            <div class="skeleton-event">
              <div class="skeleton-event-title"></div>
              <div class="skeleton-event-time"></div>
            </div>
            <div class="skeleton-event">
              <div class="skeleton-event-title"></div>
              <div class="skeleton-event-time"></div>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Show actual calendar when loaded -->
    @if (calendarLoaded && (hasGoogleAccess || hasMicrosoftAccess)) {
      <app-calendar
        [date]="timeSheetForm.get('date')?.value"
        (calendarEventSelected)="loadFromCalendar($event)"
        (multipleEventsSelected)="handleMultipleEventsSelected($event)"
        [class.collapsed]="!isCalendarExpanded">
      </app-calendar>
    }
  </div>

  <div class="right">
    <div class="time-sheet" [class.with-panel]="showCalendar">
      <!-- Calendar connection status -->
      @if (!calendarLoading && (!hasGoogleAccess && !hasMicrosoftAccess)) {
      <div class="calendar-connection-status">
        <mat-icon>cloud_off</mat-icon>
        <div class="status-content">
          <span>No calendar connected. Connect your calendar to import events.</span>
          <div class="connection-buttons">
            <button mat-raised-button color="primary" (click)="requestCalendarAccess()">
              <mat-icon>calendar_today</mat-icon>
              Connect Google
            </button>
            <button mat-raised-button color="primary" (click)="requestMicrosoftCalendarAccess()">
              <mat-icon>event</mat-icon>
              Connect Microsoft
            </button>
          </div>
        </div>
      </div>
      }

      <!-- Completed entries chip list -->
      @if (filteredTimesheets.length > 0) {
        <mat-chip-listbox class="timesheet-chips">
          <mat-chip (click)="addItem(false)" class="selected" color="primary" [highlighted]="!timeSheetForm.value.key">
            New Entry
          </mat-chip>
          
          @for (item of filteredTimesheets; track item) {
            <mat-chip
              [class.selected]="item.key === timeSheetForm.value.key"
              (click)="itemSelected(item)"
              color="primary"
              [highlighted]="item.key === timeSheetForm.value.key"
            >
              {{ item.notes | elipse:20}} -
              {{ item.from }} - {{ item.to }}
              <button matChipRemove [appConfirm]="'Are you sure you want to delete this time entry?'"
              (confirm)="delete(item)">
                <mat-icon>cancel</mat-icon>
              </button>
            </mat-chip>
          }
        </mat-chip-listbox>
      }

 
      <div class="time-sheet-inner" [formGroup]="timeSheetForm">
        <!-- Heading moved to edit.component.html h1 tag -->
        <!-- Hidden datepicker for programmatic access -->
        <input
          style="position: absolute; width: 0; height: 0; opacity: 0; pointer-events: none;"
          matInput
          formControlName="date"
          [matDatepicker]="picker"
        />
        <mat-datepicker #picker></mat-datepicker>

        <!-- Project Field with Skeleton Loader -->
        @if (isProjectsLoading) {
          <div class="skeleton-loader">
            <div class="skeleton-field-input"></div>
            <br>
            <div class="skeleton-field-input"></div>
          </div>
        } @else if ((filteredClients.length > 0 && billableItems.length > 0)) {
          <div class="form-field-container">
            <mat-form-field class="required full-width">
              <mat-label> Project </mat-label>

              <mat-select
                formControlName="billableItem"
                (selectionChange)="billableItemSelected($event)"
                >
                @for (client of filteredClients; track client) {
                  <mat-optgroup
                    [label]="client.name"
                    class="select-label"
                    >
                    @for (item of getClientItems(client); track item) {
                      <mat-option
                        [value]="item.key"
                        >
                        {{ item.name }}
                      </mat-option>
                    }
                  </mat-optgroup>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }

        <!-- Item Field with Skeleton Loader -->
        @if (isItemsLoading) {
          <div class="skeleton-loader">
            <div class="skeleton-field-label"></div>
            <div class="skeleton-field-input"></div>
          </div>
        } @else if (!timeSheetForm.value.billableItem && !isProjectsLoading) {
          <!-- Show a disabled form field when no project selected yet -->
          <div class="form-field-container">
            <mat-form-field class="required full-width">
              <mat-label> Item </mat-label>
              <mat-select formControlName="item" [disabled]="true">
                <mat-option value="">
                  Please select a project first
                </mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        } @else if (!isProjectsLoading) {
          <div class="form-field-container" >
            <mat-form-field class="required full-width">
              <mat-label> Item </mat-label>
              <mat-select formControlName="item" [disabled]="!timeSheetForm.value.billableItem">
                @for (item of items; track item) {
                  <mat-option [value]="item.name">
                    {{ item.name }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>
        }

        @if (!multipleSelected) {
          <mat-form-field>
            <mat-label> Notes </mat-label>
            <textarea formControlName="notes" matInput placeholder=""></textarea>
          </mat-form-field>
        }

        @if (!multipleSelected) {
          <div class="time-row">
            <mat-form-field class="required">
              <mat-label> From </mat-label>
              <input
                formControlName="from"
                matInput
                type="time"
                placeholder="From"
                />
            </mat-form-field>
            <mat-form-field>
              <mat-label> To </mat-label>
              <input formControlName="to" matInput type="time" placeholder="To" />
            </mat-form-field>
          </div>
        }

        @if (!multipleSelected) {
          <div class="buttons">
            @if (isEdit && !hideAdd) {
              <div class="add-button" (click)="addItem(true)">
                <mat-icon color="accent" aria-hidden="false">add_circle_outline</mat-icon>
              </div>
            }
            @if (!timeSheetForm.value.from && !timeSheetForm.value.to) {
              <button
                mat-raised-button
                color="primary"
                [disabled]="!timeSheetForm.value.item"
                (click)="startTimer()"
              >
                Start Timer
              </button>
            }
            @if (timeSheetForm.value.from && !timeSheetForm.value.to) {
              <button
                mat-raised-button
                [disabled]="startButtonDelay || !timeSheetForm.valid"
                color="primary"
                (click)="stopTimer()"
              >
                Stop Timer
              </button>
            }
            @if (timeSheetForm.value.from && timeSheetForm.value.to) {
              <button
                mat-raised-button
                color="primary"
                color="warn"
                (click)="addHour()"
              >
                Add Hour
              </button>
            }
            @if (timeDifference) {
              <div class="time-diff">
                {{ timeDifference }}
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>
</div>
</mat-dialog-content>

<div mat-dialog-actions class="buttons-bottom" align="end">
  <button mat-button (click)="onNoClick()">Close</button>

  @if (multipleSelected) {
    <div class="buttons">
      <button mat-raised-button color="primary" (click)="saveMultiple()">
        <span>Save Entries</span>
      </button>
    </div>
  }
  @if (isEdit && !multipleSelected) {
    <button
      color="warn"
      mat-raised-button
      mat-button
      (click)="deleteItem()"
    >
      Delete
    </button>
  }
  @if (!multipleSelected) {
    <!-- Show Update button for all entries (including running timers) as long as form is valid -->
    @if (isEdit) {
      <button
        mat-raised-button
        [disabled]="!timeSheetForm.valid"
        color="primary"
        class="mar-right"
        (click)="save()"
      >
        <span>Update</span>
      </button>
    }
    @if (!isEdit) {
      <button
        mat-raised-button
        [disabled]="!timeSheetForm.valid"
        color="primary"
        class="mar-right"
        (click)="save()"
      >
        <span>Save</span>
      </button>
    }
  }


</div>